import { LoanParams } from 'app/models/loan-params'
import {
  Box,
  Button,
  Divider,
  Group,
  NumberInput,
  Select,
  SelectItem,
  Stack,
  Switch,
  Text,
} from '@mantine/core'
import { IconArrowLeft, IconChevronDown, IconPlus } from '@tabler/icons-react'
import CustomDatePicker from 'app/views/components/date-picker/date-picker-logic'
import PrimaryButton from 'app/views/components/buttons/PrimaryButton'
import {
  currencyOptions,
  dayBasisOptions,
  roundingOptions,
  pikOptions,
} from 'app/models/dropdown-options'
import SplitSummaryBar from './split-summary-bar'
import SplitLoanPanel from './split-loan-panel'
import FormWrapperWithSubtitle from '../common/form-wrapper-with-subtitle'
import {
  ErrorNotification,
  SuccessNotification,
} from 'app/views/components/notifications/notification'
import { useMemo, useState } from 'react'
import {
  ContractPeriod,
  FacilityParams,
  IroValues,
} from 'app/models/facility-params'
import { LoanRolloverParams } from 'app/models/loan-rollover-params'
import { LoanRolloverSplitParams } from 'app/models/loan-rollover-split-params'
import { Option } from 'app/models/dropdown-options'
import { toNumberInputValue } from 'app/utils/util-functions'
import React from 'react'
import WarningText from 'app/views/components/warning-text/warning-text'
import { getPikConfig } from '../loans/utilities/pik-config-utils'
import { deriveAllInRate, formatRatePercent, rateDiffers } from 'app/utils/rate-rounding'

type Props = {
  form: any
  close: any
  facility: FacilityParams | undefined
  loan: LoanParams
  rollover: LoanRolloverParams | undefined
  existingSplit: LoanRolloverSplitParams | undefined
  facilityindexOptions: Option[]
  createDraftRollover: () => Promise<any>
  createDraftRolloverSplit: () => Promise<any>
  submitDraftForApproval: (draft: any, splitModeHint?: boolean) => Promise<any>
  toggleSplitMode: (checked: boolean) => void
  addSplitPanel: () => void
  removeSplitPanel: (index: number) => void
  onMarginChange: (value: number | '') => void
  onInterestBaseRateChange: (value: number | '') => void
  changeContractPeriod: (event: string | null) => void
  handleWithPikChange: (event: any) => void
  changeIndexOption: (event: string | null) => void
  facilityContractPeriodOptions: SelectItem[]
  calculateInterests: (
    iro: IroValues | undefined,
    contractPeriod: ContractPeriod | undefined,
    startDate: Date,
  ) => void
  iro: IroValues | undefined
  contractPeriod: ContractPeriod | undefined
  isContractPeriodDisabled: boolean
  pikConfig: ReturnType<typeof getPikConfig>
}

export default function RolloverFormPresentation({
  form,
  close,
  facility,
  loan,
  rollover,
  existingSplit,
  iro,
  contractPeriod,
  facilityindexOptions,
  facilityContractPeriodOptions,
  isContractPeriodDisabled,
  createDraftRollover,
  createDraftRolloverSplit,
  submitDraftForApproval,
  toggleSplitMode,
  addSplitPanel,
  removeSplitPanel,
  onMarginChange,
  onInterestBaseRateChange,
  changeContractPeriod,
  handleWithPikChange,
  changeIndexOption,
  calculateInterests,
  pikConfig,
}: Props) {
  const [isLoading, setIsLoading] = useState(false)
  const accuredInterest = useMemo(() => {
    if (!form.values.withPik) {
      return Number(loan?.accrualInterestMaturityDate ?? 0.0)
    }

    return Number(loan?.accrualInterestMaturityDate ?? 0.0) + Number(loan?.pikPremiumInterestAtMaturity ?? 0.0)

  }, [form.values.withPik, loan?.accrualInterestMaturityDate, loan?.pikPremiumInterestAtMaturity])

  const onSubmit = async () => {
    setIsLoading(true)
    const wasSplit = form.values.splitMode
    const result = wasSplit
      ? await createDraftRolloverSplit()
      : await createDraftRollover()
    setIsLoading(false)
    if (result) close()
  }

  const saveAndSubmit = async () => {
    setIsLoading(true)
    // Capture splitMode up-front — createDraft* calls form.reset() inside
    // their finally block, which flips form.values.splitMode back to false
    // before we get a chance to route the submit step.
    const wasSplit = form.values.splitMode
    const response: any = wasSplit
      ? await createDraftRolloverSplit()
      : await createDraftRollover()
    try {
      if (!response) {
        return
      }
      const result: any = await submitDraftForApproval(
        response.payload?.data,
        wasSplit,
      )
      if (result.success) {
        SuccessNotification({
          title: wasSplit
            ? 'Successfully Submitted Rollover Split'
            : 'Successful Rollover Submitted',
          message: wasSplit
            ? 'Loan rollover split submitted successfully'
            : 'Loan rollover submitted successfully',
        })
      } else {
        ErrorNotification({
          title: wasSplit
            ? 'Rollover split created but not submitted.'
            : 'Rollover created but not submitted.',
          message: result.payload ?? 'Could not submit Loan Rollover',
        })
      }
    } finally {
      close()
      setIsLoading(false)
    }
  }

  // --- Summary bar values (shown in both regular and split mode) ---
  // In split mode, sum panel amounts; in flat mode, use form.values.amount which
  // in PIK-capitalization flows already reflects existingAmount + pikAmount.
  const existingLoanAmountForSummary = Number(loan?.amount ?? 0)
  const newLoansTotal = form.values.splitMode
    ? (form.values.newLoans ?? []).reduce(
      (sum: number, nl: any) => sum + (Number(nl?.amount) || 0),
      0,
    )
    : Number(form.values.amount ?? 0)
  // PIK capitalized amount mirrors `calculatePikAmounts` in the form logic
  // and the resulting `form.values.amount` on the flat path:
  //   pikCapitalized = (accrualInterestMaturityDate + pikPremiumInterestAtMaturity)
  //                    * pikPercentage / 100
  // Both summands are needed because PIK_Scheduled / PIK_Optional loans can
  // carry a non-zero pikPremium, in which case the ledger splits the total
  // accrued interest into a base portion (accrualInterestMaturityDate) and
  // a premium portion (pikPremiumInterestAtMaturity). The displayed Accrued
  // Interest field already shows the sum.
  const pikCapitalizedAmount =
    (Number(loan?.accrualInterestMaturityDate ?? 0) +
      Number(loan?.pikPremiumInterestAtMaturity ?? 0)) *
    (Number(form.values.pikPercentage ?? 0) / 100)
  // Block save/submit when the user's allocation doesn't match what DAML
  // requires. Tolerance mirrors the CreateRolloverSplit / ApproveRolloverSplit
  // assertions (0.01).
  const pikTotalMismatch =
    form.values.withPik === true &&
    Math.abs(
      newLoansTotal - (existingLoanAmountForSummary + pikCapitalizedAmount),
    ) >= 0.01

  // Block save/submit when the new-loan PIK rate decomposition doesn't add up:
  //   pikInterestPercent + cashInterestPercent must equal allInRateWithPikPremium.
  const pikRateAllocationMismatch = (() => {
    const pikFieldsApply =
      pikConfig.showNewLoanPikFields ||
      (pikConfig.showNewLoanPikFieldsWhenEnabled &&
        form.values.newLoanWithPik === true)
    if (!pikFieldsApply) return false
    if (form.values.splitMode) {
      // Split: any panel with a mismatch blocks save.
      return (form.values.newLoans ?? []).some((nl: any) => {
        const panelPikFieldsApply =
          pikConfig.showNewLoanPikFields ||
          (pikConfig.showNewLoanPikFieldsWhenEnabled &&
            nl?.newLoanWithPik === true)
        if (!panelPikFieldsApply) return false
        const pikInterest = Number(nl?.pikInterestPercent) || 0
        const cashInterest = Number(nl?.cashInterestPercent) || 0
        const allInRateWithPik =
          Number(nl?.allInRateWithPikPremium) || Number(nl?.allInRate) || 0
        return (
          Math.abs(pikInterest + cashInterest - allInRateWithPik) >= 0.01
        )
      })
    }
    const pikInterest = Number(form.values.pikInterestPercent) || 0
    const cashInterest = Number(form.values.cashInterestPercent) || 0
    const allInRateWithPik =
      Number(form.values.allInRateWithPikPremium) ||
      Number(form.values.allInRate) ||
      0
    return Math.abs(pikInterest + cashInterest - allInRateWithPik) >= 0.01
  })()

  const changePikPercentage = (e: any) => {
    form.setFieldValue('pikPercentage', e)
    const totalInterestAmount = Number(loan?.accrualInterestMaturityDate ?? 0) + Number(loan?.pikPremiumInterestAtMaturity ?? 0)
    const pikAmount = totalInterestAmount * (e / 100)
    const interestPaymentAmount = totalInterestAmount - pikAmount
    form.setFieldValue('pikAmount', pikAmount)
    form.setFieldValue('interestPaymentAmount', interestPaymentAmount)
    const amount = Number(loan.amount) + pikAmount
    form.setFieldValue('amount', amount)
  }

  const changePikAmount = (e: any) => {
    let pikAmount = Number(e)
    const totalInterestAmount = Number(loan?.accrualInterestMaturityDate ?? 0) + Number(loan?.pikPremiumInterestAtMaturity ?? 0)

    let pikPercentage = (pikAmount / totalInterestAmount) * 100
    if (pikPercentage > 100) {
      pikPercentage = 100
      pikAmount = totalInterestAmount
    }

    const interestPaymentAmount = totalInterestAmount - pikAmount
    const amount = Number(loan.amount) + pikAmount

    form.setFieldValue('pikAmount', pikAmount)
    form.setFieldValue('pikPercentage', Number(pikPercentage))
    form.setFieldValue('interestPaymentAmount', interestPaymentAmount)
    form.setFieldValue('amount', amount)
  }

  // All-In Rate with PIK Premium for display: ROUNDED Base Rate + Margin + CAS + PIK Premium.
  // This is what users see in the "All In Rate" field. The rounded base rate is derived
  // alongside so the two can never disagree (the ledger derives both on save).
  // The derived fields are dependencies too (guarded by `rateDiffers`) so a
  // saved draft re-hydrated after this effect gets corrected instead of
  // leaving its stored all-in rate in place.
  const derivedRates = deriveAllInRate({
    interestBaseRate: form.values.interestBaseRate,
    rounding: form.values.rounding,
    margin: form.values.margin,
    casValue: form.values.casValue,
    pikPremium: form.values.pikPremium,
  })
  React.useEffect(() => {
    const { roundedBaseRate, allInRate } = derivedRates
    if (rateDiffers(form.values.roundedBaseRate, roundedBaseRate)) {
      form.setFieldValue('roundedBaseRate', roundedBaseRate)
    }
    if (rateDiffers(form.values.allInRate, allInRate)) {
      form.setFieldValue('allInRate', allInRate)
    }
    if (rateDiffers(form.values.allInRateWithPikPremium, allInRate)) {
      form.setFieldValue('allInRateWithPikPremium', allInRate)
    }
  }, [derivedRates.roundedBaseRate, derivedRates.allInRate, form.values.roundedBaseRate, form.values.allInRate, form.values.allInRateWithPikPremium])

  return (
    <FormWrapperWithSubtitle
      title={
        (rollover || existingSplit ? 'Edit' : 'Create') +
        ' Rollover' +
        (pikConfig.showPikConfiguration ? ' w/PIK' : '')
      }
      subtitle=""
    >
      <div className="content">
        <form onSubmit={form.onSubmit(() => onSubmit())}>
          <div className="create-new">
            <Stack spacing="xl">
              {rollover && rollover?.status !== 'Draft' ? (
                <Text className="topFormErrorText">
                  Rollover has already been submitted
                </Text>
              ) : null}
              <Box mb={20}>
                <SplitSummaryBar
                  maturingPrincipal={existingLoanAmountForSummary}
                  newLoansTotal={newLoansTotal}
                  newLoansLabel={
                    form.values.splitMode
                      ? 'New Loans Total'
                      : 'New Loan Principal'
                  }
                  withPik={form.values.withPik === true}
                  pikCapitalizedAmount={pikCapitalizedAmount}
                />
              </Box>
              {(pikConfig.showExistingLoanPikToggle || pikConfig.isPikScheduled || pikConfig.isPikAll) && (
                <Group noWrap>
                  {pikConfig.showExistingLoanPikToggle && (
                    <div style={{ width: '100%' }}>
                      <Stack style={{ marginTop: '15px' }}>
                        <Group>
                          <Switch
                            labelPosition="left"
                            label=" PIK (ON/OFF)"
                            size="lg"
                            checked={form.values.withPik}
                            onChange={handleWithPikChange}
                            value={form.values.withPik}
                            classNames={{ track: 'primary-switch-track' }}
                          />
                        </Group>
                      </Stack>
                    </div>
                  )}
                  <Select
                    searchable
                    readOnly
                    label="PIK Options"
                    w="100%"
                    data={[...pikOptions]}
                    value={facility?.pikOption}
                  />
                  <NumberInput
                    hideControls
                    withAsterisk
                    readOnly
                    precision={2}
                    w="100%"
                    label="Accrued Interest"
                    id="accruead Interest"
                    parser={value =>
                      value ? value.replace(/\$\s?|(,*)/g, '') : ''
                    }
                    formatter={value =>
                      !Number.isNaN(parseFloat(value ?? ''))
                        ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                        : ''
                    }
                    value={
                      accuredInterest
                    }
                  />
                </Group>
              )}
              {!form.values.splitMode && (<Stack
                spacing={0}
                sx={{
                  '& > *:not(:first-of-type)': { marginTop: 6 },
                }}
              >
                <Group noWrap>
                  <Select
                    searchable
                    withAsterisk
                    label="Index Rate"
                    placeholder="Select Index Rate"
                    w="100%"
                    data={facilityindexOptions}
                    {...form.getInputProps('interestRateOption')}
                    onChange={e => {
                      changeIndexOption(e)
                    }}
                  />
                  <Select
                    searchable
                    withAsterisk
                    readOnly
                    data={currencyOptions}
                    label="Select Currency"
                    placeholder="Currency"
                    w="100%"
                    {...form.getInputProps('currency')}
                  />
                  {!form.values.splitMode &&
                    !!facility?.baseCurrency &&
                    !!form.values.currency &&
                    form.values.currency !== facility.baseCurrency && (
                      <NumberInput
                        hideControls
                        w="100%"
                        precision={6}
                        label="FX Rate"
                        placeholder="Enter FX rate"
                        min={0}
                        {...form.getInputProps('fxRate')}
                        value={toNumberInputValue(form.values.fxRate)}
                      />
                    )}
                  {form.values.withPik && (
                    <NumberInput
                      hideControls
                      withAsterisk
                      w="100%"
                      precision={5}
                      min={0}
                      max={100}
                      label="PIK Percentage"
                      parser={value =>
                        value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
                      }
                      formatter={value =>
                        !Number.isNaN(parseFloat(value ?? ''))
                          ? value + ' %'
                          : '%'
                      }
                      {...form.getInputProps('pikPercentage')}
                      onChange={e => {
                        changePikPercentage(e)
                      }}
                    />
                  )}

                  {!form.values.withPik && (
                    <NumberInput
                      hideControls
                      withAsterisk
                      readOnly={form.values.withPik}
                      precision={2}
                      w="100%"
                      label="Amount"
                      id="amount"
                      placeholder="Enter Amount"
                      parser={value =>
                        value ? value.replace(/\$\s?|(,*)/g, '') : ''
                      }
                      formatter={value =>
                        !Number.isNaN(parseFloat(value ?? ''))
                          ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                          : ''
                      }
                      {...form.getInputProps('amount')}
                    />
                  )}
                </Group>

                {form.values.withPik && (
                  <Group noWrap>
                    <NumberInput
                      hideControls
                      precision={2}
                      w="100%"
                      label="PIK Amount"
                      parser={value =>
                        value ? value.replace(/\$\s?|(,*)/g, '') : ''
                      }
                      formatter={value =>
                        !Number.isNaN(parseFloat(value ?? ''))
                          ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                          : ''
                      }
                      {...form.getInputProps('pikAmount')}
                      onChange={e => {
                        changePikAmount(e)
                      }}
                    />
                    <NumberInput
                      hideControls
                      readOnly={form.values.withPik}
                      precision={2}
                      w="100%"
                      label="Interest Payment"
                      parser={value =>
                        value ? value.replace(/\$\s?|(,*)/g, '') : ''
                      }
                      formatter={value =>
                        !Number.isNaN(parseFloat(value ?? ''))
                          ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                          : ''
                      }
                      {...form.getInputProps('interestPaymentAmount')}
                    />
                    <NumberInput
                      hideControls
                      readOnly={form.values.withPik}
                      precision={2}
                      w="100%"
                      label="Rollover Amount"
                      value={
                        Number(form.values.pikAmount ?? 0) +
                        Number(loan?.amount ?? 0)
                      }
                      parser={value =>
                        value ? value.replace(/\$\s?|(,*)/g, '') : ''
                      }
                      formatter={value =>
                        !Number.isNaN(parseFloat(value ?? ''))
                          ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                          : ''
                      }
                    />
                  </Group>
                )}
                <Select
                  searchable
                  disabled={isContractPeriodDisabled}
                  label="Contract Period"
                  placeholder="Select Contract Period"
                  w="100%"
                  data={facilityContractPeriodOptions}
                  {...form.getInputProps('contractPeriod')}
                  onChange={e => {
                    changeContractPeriod(e)
                  }}
                />
                <Group noWrap>
                  <CustomDatePicker
                    required
                    label={'Start Date'}
                    date={form.values.startDate}
                    setDate={(value: any) => {
                      form.setFieldValue('startDate', value)
                      calculateInterests(iro, contractPeriod, value)
                    }}
                    holidayCalendars={facility?.holidayCalendar ?? []}
                  />

                  <CustomDatePicker
                    required
                    label={'Maturity Date'}
                    date={form.values.maturity}
                    setDate={(value: any) =>
                      form.setFieldValue('maturity', value)
                    }
                    holidayCalendars={facility?.holidayCalendar ?? []}
                  />

                  <Select
                    searchable
                    readOnly
                    data={dayBasisOptions}
                    label="Day basis"
                    w="100%"
                    {...form.getInputProps('dayBasis')}
                  />
                </Group>
                {(form.isTouched('startDate') || form.isTouched('maturity')) &&
                  form.values.maturity &&
                  new Date(form.values.startDate) >=
                  new Date(form.values.maturity) ? (
                  <div className="topFormErrorText">
                    Start Date Must Be Before Maturity Date
                  </div>
                ) : null}

                <Group noWrap>
                  <NumberInput
                    hideControls
                    w="100%"
                    precision={5}
                    label="Interest Rate "
                    parser={value =>
                      value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
                    }
                    formatter={value =>
                      !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
                    }
                    {...form.getInputProps('interestBaseRate')}
                    onChange={onInterestBaseRateChange}
                  />

                  <NumberInput
                    hideControls
                    w="100%"
                    precision={5}
                    label="Interest Rate with Rounding"
                    parser={value =>
                      value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
                    }
                    formatter={value =>
                      !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
                    }
                    {...form.getInputProps('roundedBaseRate')}
                  />

                  <Select
                    searchable
                    data={roundingOptions}
                    readOnly
                    label="Interest Base Rate Rounding"
                    w="100%"
                    {...form.getInputProps('rounding')}
                  />
                </Group>

                <Group noWrap>
                  <NumberInput
                    hideControls
                    w="100%"
                    precision={5}
                    label="Margin"
                    parser={value =>
                      value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
                    }
                    formatter={value =>
                      !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
                    }
                    {...form.getInputProps('margin')}
                    onChange={onMarginChange}
                  />

                  {form.values.casValue !== undefined &&
                    form.values.casValue !== 0 && (
                      <NumberInput
                        hideControls
                        readOnly
                        w="100%"
                        precision={5}
                        label="CAS Rate"
                        parser={value =>
                          value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
                        }
                        formatter={formatRatePercent}
                        {...form.getInputProps('casValue')}
                      />
                    )}

                  <NumberInput
                    hideControls
                    readOnly
                    className="applyGrayBackground"
                    w="100%"
                    precision={5}
                    label="All In Rate"
                    parser={value =>
                      value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
                    }
                    // Shown as stored: margin, CAS and PIK premium are not rounded.
                    formatter={formatRatePercent}
                    {...form.getInputProps('allInRate')}
                  />
                </Group>

                <Divider style={{ marginTop: 15 }} />

                <Button
                  style={{ marginTop: 15, width: '30%' }}
                  radius={10}
                  h="auto"
                  p={10}
                  variant="default"
                  leftIcon={<IconChevronDown size={16} color="#32439A" />}
                  onClick={() => toggleSplitMode(true)}
                  styles={{
                    label: { fontSize: '14px !important', lineHeight: 1.5 },
                  }}
                >
                  Split into multiple loans
                </Button>

                {pikConfig.showNewLoanPikToggle && (
                  <Group noWrap>
                    <div style={{ width: '100%' }}>
                      <Stack style={{ marginTop: '10px', marginBottom: '10px' }}>
                        <Group>
                          <Switch
                            labelPosition="left"
                            label="New Loan PIK (ON/OFF)"
                            size="lg"
                            checked={form.values.newLoanWithPik || false}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              form.setFieldValue('newLoanWithPik', event.currentTarget.checked)
                              if (!event.currentTarget.checked) {
                                // Reset PIK fields to 0 when toggled off
                                form.setFieldValue('pikPremium', 0)
                                form.setFieldValue('pikInterestPercent', 0)
                                form.setFieldValue('cashInterestPercent', 0)
                              } else {
                                // Only pre-populate PIK Premium when toggled on, not cash/PIK interest %
                                form.setFieldValue('pikPremium', loan?.pikPremium ? Number(loan.pikPremium) : 0)
                              }
                            }}
                            classNames={{ track: 'primary-switch-track' }}
                          />
                        </Group>
                      </Stack>
                    </div>
                  </Group>
                )}

                {/* Show PIK configuration fields for PIK_Optional when toggle is ON, or always for PIK_Scheduled */}
                {(pikConfig.showNewLoanPikFields ||
                  (pikConfig.showNewLoanPikFieldsWhenEnabled && form.values.newLoanWithPik)) && (
                    <>
                      <Group noWrap sx={{ marginBottom: '10px' }}>
                        <NumberInput
                          hideControls
                          w="100%"
                          precision={5}
                          label="PIK Premium (%)"
                          parser={value =>
                            value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
                          }
                          formatter={value =>
                            !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
                          }
                          {...form.getInputProps('pikPremium')}
                        />
                        <NumberInput
                          hideControls
                          withAsterisk
                          w="100%"
                          precision={5}
                          label="PIK Interest (%)"
                          parser={value =>
                            value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
                          }
                          formatter={value =>
                            !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
                          }
                          {...form.getInputProps('pikInterestPercent')}
                        />
                        <NumberInput
                          hideControls
                          withAsterisk
                          w="100%"
                          precision={5}
                          label="Cash Interest (%)"
                          parser={value =>
                            value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
                          }
                          formatter={value =>
                            !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
                          }
                          {...form.getInputProps('cashInterestPercent')}
                        />
                      </Group>

                      {form.values.cashInterestPercent !== undefined &&
                        form.values.pikInterestPercent !== undefined &&
                        form.values.allInRate !== undefined &&
                        (() => {
                          const pikInterest = Number(form.values.pikInterestPercent) || 0
                          const cashInterest = Number(form.values.cashInterestPercent) || 0
                          const allInRateWithPik = Number(form.values.allInRateWithPikPremium) || Number(form.values.allInRate) || 0
                          const sum = pikInterest + cashInterest
                          const tolerance = 0.01
                          const diff = Math.round(Math.abs(sum - allInRateWithPik) * 100) / 100
                          return diff >= tolerance
                        })() && (
                          <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                            <WarningText
                              type="error"
                              text="PIK Cash % + PIK Interest % must equal All In Rate"
                            />
                          </div>
                        )}
                    </>
                  )}
              </Stack>)}

              {form.values.splitMode && (
                <>
                  {/* Accrued-interest PIK/Cash allocation for the maturing loan.
                      Hidden by the !splitMode gate on the flat fields, but the
                      same pikAmount/pikPercentage feed the summary bar's PIK
                      Capitalized value, the pikTotalMismatch save guard, and the
                      split draft payload — so surfacing the inputs here lets the
                      user adjust the split in split mode. (LOS-5952) */}
                  {form.values.withPik && (
                    <Box mt={10} mb={10}>
                      <Group noWrap>
                        <NumberInput
                          hideControls
                          precision={2}
                          w="100%"
                          label="PIK Amount"
                          parser={value =>
                            value ? value.replace(/\$\s?|(,*)/g, '') : ''
                          }
                          formatter={value =>
                            !Number.isNaN(parseFloat(value ?? ''))
                              ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                              : ''
                          }
                          {...form.getInputProps('pikAmount')}
                          onChange={e => {
                            changePikAmount(e)
                          }}
                        />
                        <NumberInput
                          hideControls
                          readOnly
                          precision={2}
                          w="100%"
                          label="Interest Payment"
                          parser={value =>
                            value ? value.replace(/\$\s?|(,*)/g, '') : ''
                          }
                          formatter={value =>
                            !Number.isNaN(parseFloat(value ?? ''))
                              ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                              : ''
                          }
                          {...form.getInputProps('interestPaymentAmount')}
                        />
                      </Group>
                    </Box>
                  )}
                  <Box mt={10}>
                    <Stack
                      spacing={0}
                      sx={{
                        '& > *:not(:first-of-type)': { marginTop: 10 },
                      }}
                    >
                      {(form.values.newLoans ?? []).map(
                        (nl: any, index: number) => (
                          <SplitLoanPanel
                            key={nl?.id ?? index}
                            form={form}
                            index={index}
                            facility={facility}
                            facilityindexOptions={facilityindexOptions}
                            facilityContractPeriodOptions={
                              facilityContractPeriodOptions
                            }
                            isContractPeriodDisabled={isContractPeriodDisabled}
                            pikConfig={pikConfig}
                            onRemove={() => removeSplitPanel(index)}
                          />
                        ),
                      )}
                    </Stack>
                  </Box>

                  <Divider mt={20} />

                  <Button
                    mt={20}
                    radius={10}
                    h="auto"
                    p={10}
                    variant="default"
                    fullWidth
                    leftIcon={<IconPlus size={16} color="#32439A" />}
                    onClick={addSplitPanel}
                    styles={{
                      label: { fontSize: '14px !important', lineHeight: 1.5 },
                    }}
                  >
                    Add another loan
                  </Button>

                  <Box my={10}>
                    <Button
                      variant="subtle"
                      size="xs"
                      compact
                      leftIcon={<IconArrowLeft size={16} color="#32439A" />}
                      onClick={() => toggleSplitMode(false)}
                      styles={{ root: { color: '#32439A' } }}
                    >
                      Back to single rollover
                    </Button>
                  </Box>
                </>
              )}

              <Group noWrap mt={10}>
                <PrimaryButton
                  loading={isLoading}
                  type="submit"
                  disabled={!form.isValid() || pikTotalMismatch || pikRateAllocationMismatch}
                  text="Save"
                  w="100%"
                />
                <PrimaryButton
                  loading={isLoading}
                  onClick={() => saveAndSubmit()}
                  disabled={!form.isValid() || pikTotalMismatch || pikRateAllocationMismatch}
                  text="Save and Submit"
                  w="100%"
                />
              </Group>
            </Stack>
          </div>
        </form>
      </div>
    </FormWrapperWithSubtitle>
  )
}
